/**
 * SCRUM-318/326 follow-up — wires `traceApiSpan` (src/lib/ops-logger.ts) into
 * real request handling. Without this, the Traces screen has no data: the
 * function existed but was never called from anywhere (GAP-TRACES-ZERO-INSTRUMENTATION).
 *
 * Only spans /api/* requests that carry X-Tenant-Id — that's the only shape
 * listTraceSummaries/listOpsSpansFromDb can ever query (both filter by an
 * exact tenant_id match), so a span without one would be dead weight.
 *
 * Sampled, not exhaustive: writing a sm_platform_events row on every single
 * tenant-scoped API request platform-wide is real, unbounded write load on
 * Neon with no cleanup/TTL for this table. TRACE_SAMPLE_RATE (0-1, default
 * 0.05 = 5%) caps that. Errors (5xx or thrown) are ALWAYS traced regardless
 * of sampling — those are exactly what Traces/Errors correlation needs, and
 * they're low-volume by definition. Set TRACE_SAMPLE_RATE=0 to disable
 * sampling of successful requests entirely (errors still trace); unset or
 * invalid falls back to the 5% default.
 */
import { createMiddleware } from "@tanstack/react-start";
import { traceApiSpan } from "@/lib/ops-logger";
import { getRequestId, getTenantId } from "@/lib/sm-http";

const DEFAULT_SAMPLE_RATE = 0.05;

function sampleRate(): number {
  const raw = Number(process.env.TRACE_SAMPLE_RATE);
  if (!Number.isFinite(raw) || raw < 0 || raw > 1) return DEFAULT_SAMPLE_RATE;
  return raw;
}

export const apiTraceSpanMiddleware = createMiddleware().server(async ({ request, next }) => {
  const url = new URL(request.url);
  if (!url.pathname.startsWith("/api/")) return next();

  const tenantId = getTenantId(request);
  if (!tenantId) return next();

  const requestId = getRequestId(request);
  const startAt = new Date().toISOString();
  const start = performance.now();
  const sampled = Math.random() < sampleRate();

  try {
    const result = await next();
    const response = result instanceof Response ? result : result.response;
    const ok = !!response && response.status < 500;
    if (sampled || !ok) {
      traceApiSpan({
        tenantId,
        requestId,
        spanName: `${request.method} ${url.pathname}`,
        startAt,
        durationMs: Math.round(performance.now() - start),
        ok,
      });
    }
    return result;
  } catch (err) {
    traceApiSpan({
      tenantId,
      requestId,
      spanName: `${request.method} ${url.pathname}`,
      startAt,
      durationMs: Math.round(performance.now() - start),
      ok: false,
      meta: { errorMessage: err instanceof Error ? err.message : String(err) },
    });
    throw err;
  }
});
